import type { LucideIcon } from 'lucide-react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export type SelectCardProps = {
  label: string;
  description?: string;
  icon?: LucideIcon;
  selected: boolean;
  onClick: () => void;
};

export function SelectCard({ label, description, icon: Icon, selected, onClick }: SelectCardProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className={cn(
        'group relative flex w-full items-start gap-3 rounded-xl border-2 p-4 text-left transition-all',
        'hover:border-primary/40 hover:bg-primary/[0.02]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        selected
          ? 'border-primary bg-primary/[0.04] shadow-sm shadow-primary/10'
          : 'border-border bg-card',
      )}
    >
      {Icon && (
        <span
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors',
            selected ? 'bg-primary text-primary-foreground' : 'bg-secondary text-primary',
          )}
        >
          <Icon className="h-5 w-5" />
        </span>
      )}

      <span className="flex-1 leading-tight">
        <span className="block font-semibold text-foreground">{label}</span>
        {description && (
          <span className="mt-1 block text-sm text-muted-foreground">{description}</span>
        )}
      </span>

      <span
        className={cn(
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-all',
          selected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-background',
        )}
        aria-hidden
      >
        {selected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
      </span>
    </button>
  );
}
